import axios, {type Canceler} from 'axios';

import countBy_ from 'lodash/countBy';

import moment from 'moment';

import {DATE_FORMAT} from './odin-constants';
import {hasOdinPage} from '../../config';

type SaveCancelToken = ({cancel}: {cancel: Canceler}) => void;
type Availability = {[key: string]: MetricData};

export interface MetricListItem {
    display_name: string;
    name: string;
}

export interface MetricData {
    message?: string;
    state: 'available' | 'unavailable' | 'partially_available' | 'no_data';
}

export default class Utils {
    static ODIN_PATH = '/api/odin/proxy';
    static ODIN_AUTH = 'none';

    static request<T = unknown>(path: string, saveCancelToken?: SaveCancelToken): Promise<T> {
        return axios
            .request({
                url: Utils.ODIN_PATH + path,
                method: 'GET',
                responseType: 'json',
                withCredentials: Utils.ODIN_AUTH === 'domain',
                cancelToken: new axios.CancelToken((cancel) => {
                    if (typeof saveCancelToken === 'function') {
                        saveCancelToken({cancel});
                    }
                }),
            })
            .then((response) => {
                return response.data;
            })
            .catch((error) => {
                if (axios.isCancel(error)) {
                    return Promise.reject(error);
                }
                return Promise.reject(error.response?.data || error);
            });
    }

    static isRequestCanceled(error: unknown) {
        return axios.isCancel(error);
    }

    static checkStatus(cluster: string) {
        return Utils.request('/exists/' + cluster);
    }

    static listMetrics(cluster: string, saveCancelToken: SaveCancelToken) {
        return Utils.request<Array<MetricListItem>>(`/service_list/${cluster}`, saveCancelToken);
    }

    static getMetric(
        cluster: string,
        metric: string,
        from: Date | number,
        to: Date | number,
        saveCT: SaveCancelToken,
    ) {
        return Utils.request<Availability>(
            '/availability/' +
                cluster +
                '?metric=' +
                metric +
                '&start_time=' +
                moment(from).toISOString() +
                '&end_time=' +
                moment(to).toISOString() +
                '&return_state_without_message=0',
            saveCT,
        );
    }

    static getMetricOfThisDay(
        cluster: string,
        metric: string,
        date: string | null,
        saveCancelToken: SaveCancelToken,
    ) {
        const startTime = moment(date).startOf('day');
        const endTime = moment(startTime).add(1, 'day');
        return Utils.getMetric(
            cluster,
            metric,
            startTime.toDate(),
            endTime.toDate(),
            saveCancelToken,
        );
    }

    static prepareAvailabilityData(availability: Availability, size: number): Array<MetricData> {
        const asArray: Array<MetricData> = [];
        let property: string;
        let data: MetricData;

        function getPropertyName(i: number) {
            let res = '00000' + i;
            res = 'at_' + res.substr(res.length - 5);
            return res;
        }

        for (let i = 0; i < size; i++) {
            property = getPropertyName(i);

            if (availability && Object.hasOwnProperty.call(availability, property)) {
                data = availability[property];
            } else {
                data = {
                    state: 'no_data',
                };
            }
            asArray.push(data);
        }

        return asArray;
    }

    static computeStat(availability: Array<MetricData>) {
        const counts = countBy_(availability, ({state}) => {
            if (state === 'available') {
                return 'available';
            } else if (state === 'no_data') {
                return 'no_data';
            } else {
                return 'unavailable';
            }
        });

        const sum = availability.length;

        return {
            from: (counts['available'] || 0) / sum,
            to: (sum - (counts['unavailable'] || 0)) / sum,
        };
    }
}

export function currentDate() {
    return moment().startOf('day').format(DATE_FORMAT);
}

export const fetchClustersAvailability = hasOdinPage()
    ? () => {
          return axios.request<Array<ClusterAvailability>>({
              method: 'get',
              url: '/api/odin/clusters/availability',
          });
      }
    : () => Promise.resolve({data: [] as Array<ClusterAvailability>});

type ClusterAvailability = {
    id: string;
    availability?: 1;
};
